import React, { useEffect, useState } from 'react';
import { Loader } from '@pwc/pwc-deals-ui';
import { Constants, USER_ROLES } from '@pwc/pwc-deals-web-common';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';

import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import PageAuditFields from '../../../../common/components/PageAuditFields/PageAuditFields';
import ApprovalPill from '../../../../common/components/Pills/ApprovalPill';
import { DraftPillClientView } from '../../../../common/components/Pills/DraftPill';
import UserViewStatusPill from '../../../../common/components/Pills/UserViewStatusPill';
import VersionStatus from '../../../../common/components/Pills/VersionStatus';
import PreviewLoadingError from '../../../../common/components/PreviewLoadingError';
import ShowForRole from '../../../security/ShowForRole';
import styles from './ProjectOverviewReportPreview.module.scss';
import { getReportPreview } from '../../../../common/components/ReportPreview/ReportService';
import { projectOverviewPath } from '../../../../common/services/urlService';
import { getTopic } from '../../topicService';
import { TopBar } from '../../../../common/components/ReportPreview/TopBar';

const ProjectOverviewReportPreview = () => {
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const { projectId, contentThreadId, version, versionStatus } = useParams();
  const [reportData, setReportData] = useState([]);
  const [isReportLoading, setIsReportLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const [topic, setTopic] = useState({});

  useEffect(() => {
    async function fetchReportPreview() {
      try {
        const reportPreview = await getReportPreview(
          projectId,
          contentThreadId,
          version,
          undefined,
          true
        );
        setReportData(reportPreview);
      } catch (e) {
        setHasError(true);
      } finally {
        setIsReportLoading(false);
      }
    }

    fetchReportPreview();
  }, [contentThreadId, projectId, version]);

  useEffect(() => {
    getTopic(projectId, contentThreadId, '', versionStatus).then((item) => {
      setTopic(item);
    });
  }, [contentThreadId, projectId, versionStatus]);

  const getBackLink = () => {
    return projectOverviewPath(projectId, isPwcUser);
  };
  const topicLink = getBackLink();

  return (
    <div className={styles.reportPreview}>
      <TopBar
        backLabel="Project Overview"
        backLink={topicLink}
        entityId={contentThreadId}
      />

      <div className={styles['topics-report-preview-container']}>
        <Box mt="1.5rem">
          <div className="title-container">
            <h3>{topic.title}</h3>
          </div>
        </Box>
        <div className={styles.reportPreview__inner}>
          <div className={`${styles.reportPreview__inner__status}`}>
            {topic.contentId && (
              <>
                <div className="pills-section">
                  <ShowForRole
                    projectId={projectId}
                    roles={Constants.USER_ROLE_GROUPS.INTERNAL}
                  >
                    {topic.dateModified ? (
                      <PageAuditFields
                        dateModified={topic.dateModified}
                        modifiedBy={topic.modifiedBy}
                        shareWithAll={
                          topic.versionStatus ===
                          Constants.VERSION_STATUS.SHARED.value
                        }
                        version={topic.versionNumber}
                      />
                    ) : (
                      ''
                    )}
                  </ShowForRole>
                  <ShowForRole
                    projectId={projectId}
                    roles={Constants.USER_ROLE_GROUPS.INTERNAL}
                  >
                    <VersionStatus content={topic} allowDraftPill />
                  </ShowForRole>
                  <ShowForRole
                    projectId={projectId}
                    roles={Constants.USER_ROLE_GROUPS.CLIENT}
                  >
                    <DraftPillClientView />
                  </ShowForRole>
                  <ApprovalPill versionStatus={topic.versionStatus} />
                  <UserViewStatusPill userViewStatus={topic.userViewStatus} />
                </div>
              </>
            )}
          </div>
        </div>
        {hasError && <PreviewLoadingError />}
        <>
          {isReportLoading ? (
            <Loader dataTestId="loading-spinner" />
          ) : (
            reportData.map((image) => {
              return (
                <img
                  className="preview-image"
                  key={image}
                  src={image}
                  alt="report preview"
                />
              );
            })
          )}
        </>
      </div>
    </div>
  );
};

export default ProjectOverviewReportPreview;
